import { OnResizeStart } from 'react-moveable'
import { IMediaItem } from 'types/media'
import { clampStrokeThickness } from 'utils/text'

const calculateScaling = (initialWidth: number, width: number) => {
  return width / initialWidth
}

const calcScaledValues = (
  initialFontSize: number,
  initialStrokeThickness: number,
  initialWidth: number,
  width: number
) => {
  const ratio = width / initialWidth
  return {
    fontSize: initialFontSize * ratio,
    strokeThickness: clampStrokeThickness(initialStrokeThickness * ratio)
  }
}

export const useTextSpecificActions = (item: IMediaItem) => {
  const isText = item.type === 'text'

  const handleResizeStart = ({ datas, direction }: OnResizeStart) => {
    datas.initialWidth = item.width
    datas.initialFontSize = item.meta?.textData?.fontSize
    datas.initialStrokeThickness = item.meta?.textData?.strokeThickness ?? 0
    // Side handles (E/W): horizontal-only direction vector. Track separately
    // so handleResize/handleResizeEnd can branch — sides change wrap width;
    // corners scale fontSize like before.
    datas.isSideHandle = direction[1] === 0 && direction[0] !== 0 ? 1 : 0
  }

  const handleResize = (
    engineUpdate: Record<string, unknown>,
    datas: Record<string, number>,
    width: number
  ) => {
    if (datas.isSideHandle) {
      // Side handle: don't touch engine config during the drag. Phaser
      // displays the bitmap at `config.width × scale`, so updating
      // `config.width` per tick would stretch the existing bitmap
      // horizontally (visible glyph distortion). The CSS frame still grows
      // via `target.style.width`. Width + isAutoWidth are committed at
      // drag-end via `EditorText.commitTextWrap()`.
      return
    }
    if (datas.initialFontSize && datas.initialWidth) {
      const scaling = calculateScaling(datas.initialWidth, width)
      engineUpdate.width = item.width * scaling
      engineUpdate.height = item.height * scaling
    }
  }

  const handleResizeEnd = (
    updates: Partial<IMediaItem>,
    datas: Record<string, number>,
    width: number
  ) => {
    if (datas.isSideHandle) {
      updates.meta = {
        ...item.meta,
        textData: {
          ...item.meta.textData!,
          isAutoWidth: false
        }
      }
      return
    }
    if (datas.initialFontSize && datas.initialWidth) {
      const { fontSize, strokeThickness } = calcScaledValues(
        datas.initialFontSize,
        datas.initialStrokeThickness,
        datas.initialWidth,
        width
      )
      updates.meta = {
        ...item.meta,
        textData: {
          ...item.meta.textData!,
          fontSize,
          strokeThickness
        }
      }
    }
  }

  return {
    handleTextResizeStart: handleResizeStart,
    handleTextResize: handleResize,
    handleTextResizeEnd: handleResizeEnd,
    isText
  }
}
